import { useState, useEffect } from 'react';
import { VStack, Box, Button, Text, Popover, PopoverAnchor, PopoverContent, Input } from '@cad-arc';
import { MapLedgerEventView, MapLedgerUnitView } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { getUnitIconByUniqueValueKey, getUnitUniqueValueKey } from './map-entity-details/helpers';
import { useEsriMapStore } from '../hooks/use-esri-map-store';
import eventAssignedInactive from '../../esri/images/event-icons/assigned/event_assigned_inactive.svg';
import eventUnassignedInactive from '../../esri/images/event-icons/unassigned/event_unassigned_inactive.svg';
import styles from './event-and-unit-select.module.css';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { useApplicationType } from '../../common/hooks/use-application-type';

interface OptionItem {
    value: number;
    label: string;
    icon: string;
    subtitle: string | undefined;
    type: EsriMapEntityTypeEnum;
    iconKey?: string;
}
const INITIAL_LENGTH_COUNT = 5;

export const EventAndUnitSearchSelect = observer(function ({
    onChange,
    onBlur,
    unitsSearchResult,
    eventsSearchResult,
    usersSearchResult,
    selectUnit,
    selectEvent,
    selectUnitUser,
}: {
    onChange: (keyword: string) => void;
    onBlur: () => void;
    unitsSearchResult: MapLedgerUnitView[];
    eventsSearchResult: MapLedgerEventView[];
    usersSearchResult: MapLedgerUnitView[];
    selectUnit: (unitId: number, shouldResetZoomLevel?: boolean) => void;
    selectEvent: (eventId: number, shouldResetZoomLevel?: boolean) => void;
    selectUnitUser: (unitUserId: number, shouldResetZoomLevel?: boolean) => void;
}): React.ReactElement {
    const mapStore = useEsriMapStore();
    const [query, setQuery] = useState<string>('');
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [lengthCount, setLengthCount] = useState(INITIAL_LENGTH_COUNT);
    const { isFirstResponder } = useApplicationType();
    const size = isFirstResponder ? 'lg' : 'sm';

    const handleEntityClick = (type: EsriMapEntityTypeEnum, value: number, label: string) => {
        setQuery(label);
        onChange(label);
        setIsPopoverOpen(false);
        setLengthCount(INITIAL_LENGTH_COUNT);

        if (type === EsriMapEntityTypeEnum.USER) {
            selectUnitUser(value, true);
            return;
        }

        if (type === EsriMapEntityTypeEnum.UNIT) {
            selectUnit(value, true);
            mapStore.setIsFollowModeEnabled(true);
            return;
        }

        selectEvent(value, true);
    };

    const renderSearchItem = ({ value, label, icon, subtitle, type, iconKey }: OptionItem) => {
        return (
            <Box
                data-testid={`MAP_SEARCH_OPTION_${value}`}
                className={styles.searchItem}
                key={value}
                onClick={() => handleEntityClick(type, value, label)}
            >
                <VStack alignItems="flex-start">
                    <Text variant="headingXs">
                        {type} {label}
                    </Text>
                    <Text variant="caption">{subtitle}</Text>
                </VStack>
                <img src={icon} alt="icon" data-icon-key={iconKey} style={{ width: 40 }} />
            </Box>
        );
    };

    useEffect(
        function updateSearchResult() {
            const unitsOptions = unitsSearchResult.map((unit: MapLedgerUnitView) => {
                const mapViewportUnit = mapStore.getMapViewportUnit(unit.entityId);
                const unitUniqueValueKey = getUnitUniqueValueKey({
                    selectedMapLedgerUnit: unit,
                    isLocationPotentiallyInaccurate:
                        mapViewportUnit?.isLocationPotentiallyInaccurate,
                    isUser: false,
                });
                const unitOrUserIcon = getUnitIconByUniqueValueKey(unitUniqueValueKey);
                return {
                    value: unit.entityId,
                    label: unit.callSign || '',
                    subtitle: unit.personnel,
                    icon: unitOrUserIcon,
                    type: EsriMapEntityTypeEnum.UNIT,
                    iconKey: unitOrUserIcon,
                };
            });
            const eventsOptions = eventsSearchResult.map((event: MapLedgerEventView) => {
                const hasAssignedUnits = event.assignedUnits?.length;
                const eventIcon = hasAssignedUnits
                    ? eventAssignedInactive
                    : eventUnassignedInactive;
                return {
                    value: event.entityId,
                    label: event.agencyEventNumber || '',
                    subtitle: event.cfsDescription,
                    icon: eventIcon,
                    type: EsriMapEntityTypeEnum.EVENT,
                    iconKey: hasAssignedUnits ? 'Assigned' : 'Unassigned',
                };
            });
            const usersOptions = usersSearchResult.map((user: MapLedgerUnitView) => {
                const mapViewportUnit = mapStore.getMapViewportUnit(user.entityId);
                const unitUniqueValueKey = getUnitUniqueValueKey({
                    selectedMapLedgerUnit: user,
                    isLocationPotentiallyInaccurate:
                        mapViewportUnit?.isLocationPotentiallyInaccurate,
                    isUser: true,
                });
                const unitOrUserIcon = getUnitIconByUniqueValueKey(unitUniqueValueKey);
                return {
                    value: user.entityId,
                    label: user.personnel || '',
                    subtitle: user.callSign,
                    icon: unitOrUserIcon,
                    type: EsriMapEntityTypeEnum.USER,
                    iconKey: unitOrUserIcon,
                };
            });

            setOptions([...usersOptions, ...unitsOptions, ...eventsOptions]);
        },
        [unitsSearchResult, eventsSearchResult, mapStore, usersSearchResult]
    );

    const handlePopoverOpen = () => {
        if (!isPopoverOpen) {
            setIsPopoverOpen(true);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const keyword = e?.target?.value ?? '';
        setQuery(keyword);
        setLengthCount(INITIAL_LENGTH_COUNT);
        if (keyword === '') {
            setOptions([]);
        }
        onChange(keyword);
    };

    return (
        <Popover isOpen={isPopoverOpen} hasArrow={false}>
            <PopoverAnchor asChild>
                <Input
                    data-testid="MAP_EVENTS_AND_UNITS_SEARCH"
                    aria-label="Search events and units"
                    leftIcon="Search"
                    placeholder="Events and Units"
                    value={query}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={handlePopoverOpen}
                />
            </PopoverAnchor>
            <PopoverContent
                align="start"
                side="bottom"
                hasPadding={false}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onPointerDownOutside={() => {
                    setIsPopoverOpen(false);
                    setLengthCount(INITIAL_LENGTH_COUNT);
                }}
            >
                <Box w="285px">
                    {options.length < 1 ? (
                        <Box
                            w="285px"
                            height="50px"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Text fontSize={size} color="secondary">
                                {query !== '' ? `No Event/Unit found` : 'Start typing to search'}
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Box style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                {options.slice(0, lengthCount).map((el) => renderSearchItem(el))}
                            </Box>
                            {lengthCount < options.length && (
                                <Box padding="8px 0px" textAlign="center">
                                    <Button
                                        leadingVisual="Refresh"
                                        variant="ghost"
                                        onClick={() => {
                                            setLengthCount((prevCount) => prevCount + 5);
                                        }}
                                    >
                                        Show more results
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </PopoverContent>
        </Popover>
    );
});
